<template>
  <b-sidebar
    id="create-employee-sidebar"
    v-model="ownControl"
    :bg-variant="isDarkSkin ? 'dark' : 'white'"
    sidebar-class="sidebar-xl"
    shadow
    backdrop
    right
    :title="$t('Create Employee')"
    header-class="text-primary"
    @hidden="closeSidebar(false)"
  >
    <template #default>
      <ValidationObserver ref="form-employee">
        <b-form class="p-2">
          <!-- Personal Information -->
          <template>
            <b-card>
              <template #header>
                <b-card-title>{{ $t("PERSONAL INFORMATION") }}</b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <ValidationProvider name="Photo">
                      <b-form-group :label="$t('Photo')" label-for="photo">
                        <div
                          class="d-flex justify-content-center align-content-center mb-1"
                        >
                          <b-avatar
                            size="132"
                            square
                            :src="userData.file"
                            width="100"
                          />
                        </div>

                        <b-form-file
                          :placeholder="$t('Choose a file...')"
                          no-drop
                          accept="image/*"
                          @change="onImageChange"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="9">
                    <b-row>
                      <!-- Doc Type-->
                      <b-col md="3">
                        <ValidationProvider
                          v-slot="{ errors }"
                          name="DocType"
                          rules="required"
                        >
                          <b-form-group
                            :label="$t('Doc Type')"
                            label-for="doc-type"
                            :state="errors[0] ? false : null"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <v-select
                              v-if="finishedLoading"
                              v-model="userData.document_type"
                              label="name"
                              :reduce="(val) => val.id"
                              :options="documentTypes"
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                      <!-- Document Number -->
                      <b-col md="3">
                        <ValidationProvider
                          v-slot="{ errors, valid }"
                          name="NumberDoc"
                          rules="required"
                        >
                          <b-form-group
                            :label="$t('Doc Number')"
                            label-for="number-doc"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <b-form-input
                              v-if="finishedLoading"
                              id="number-doc"
                              v-model="userData.document_number"
                              trim
                              :state="errors[0] ? false : valid ? true : null"
                              :disabled="!docTypeSelected"
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <b-col md="3">
                        <!-- First Name -->
                        <ValidationProvider
                          v-slot="{ errors, valid }"
                          name="First Name"
                          rules="required"
                        >
                          <b-form-group
                            :label="$t('First Name')"
                            label-for="first-name"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <b-form-input
                              v-if="finishedLoading"
                              id="first-name"
                              v-model="userData.first_name"
                              name="first-name"
                              :state="errors[0] ? false : valid ? true : null"
                              trim
                              :disabled="disableInput"
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <!-- MiddleName -->
                      <!-- <b-col md="3">
                        <ValidationProvider name="MiddleName">
                          <b-form-group
                            label="Middle Name"
                            label-for="middle-name"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <b-form-input
                              v-if="finishedLoading"
                              id="middle-name"
                              v-model="userData.middle_name"
                              trim
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col> -->
                      <!-- LastName -->
                      <b-col md="3">
                        <ValidationProvider
                          v-slot="{ errors, valid }"
                          name="LastName"
                          rules="required"
                        >
                          <b-form-group
                            :label="$t('Last Name')"
                            label-for="last-name"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <b-form-input
                              v-if="finishedLoading"
                              id="last-name"
                              v-model="userData.last_name"
                              trim
                              :state="errors[0] ? false : valid ? true : null"
                              :disabled="disableInput"
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>
                    <b-row>
                      <!--  DOB -->
                      <b-col md="3">
                        <ValidationProvider name="DOB">
                          <b-form-group label="DOB" label-for="dob">
                            <b-skeleton v-if="!finishedLoading" type="input" />

                            <kendo-datepicker
                              v-if="finishedLoading"
                              class="form-control-kendo"
                              :class="[isDarkSkin ? ' bg-transparent' : '']"
                              :format="'MM/dd/yyyy'"
                              v-model="userData.dob"
                              v-mask="'##/##/####'"
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <b-col md="3">
                        <!-- Gender-->
                        <ValidationProvider
                          v-slot="{ errors }"
                          name="Gender"
                          rules="required"
                        >
                          <b-form-group
                            :label="$t('Gender')"
                            label-for="gender"
                            :state="errors[0] ? false : null"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <v-select
                              v-if="finishedLoading"
                              v-model="userData.gender"
                              :reduce="(val) => val.id"
                              :options="gender"
                            >
                              <template #selected-option="{ gender }">
                                <span class="text-truncate">{{
                                  $t(gender)
                                }}</span>
                              </template>
                              <template #option="{ gender }">
                                <span class="text-truncate">{{
                                  $t(gender)
                                }}</span>
                              </template>
                            </v-select>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <b-col md="3">
                        <!-- Marital Status-->
                        <ValidationProvider name="marital ">
                          <b-form-group
                            :label="$t('Marital Status')"
                            label-for="marital"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <v-select
                              v-if="finishedLoading"
                              v-model="userData.martial"
                              :reduce="(val) => val.id"
                              :options="maritals"
                            >
                              <template #selected-option="{ name }">
                                <span class="text-truncate">{{
                                  $t(name)
                                }}</span>
                              </template>
                              <template #option="{ name }">
                                <span class="text-truncate">{{
                                  $t(name)
                                }}</span>
                              </template>
                            </v-select>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <!-- Blood Type-->
                      <b-col md="3">
                        <ValidationProvider name="BloodType">
                          <b-form-group
                            :label="$t('Blood Type')"
                            label-for="blood_type"
                          >
                            <b-skeleton v-if="!finishedLoading" type="input" />
                            <b-form-input
                              v-if="finishedLoading"
                              id="blood_type"
                              v-model="userData.blood_type"
                              trim
                            />
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <!-- CONTACT INFORMATION -->
          <template>
            <b-card>
              <template #header>
                <b-card-title> {{ $t("CONTACT INFORMATION") }} </b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="6">
                    <!-- Mailing Address -->
                    <ValidationProvider name="MailingAddress">
                      <b-form-group
                        :label="$t('Mailing Address')"
                        label-for="mailing-address"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="mailing-address"
                          v-model="userData.address"
                          name="mailing-address"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Country -->
                  <b-col md="3">
                    <ValidationProvider name="Country">
                      <b-form-group :label="$t('Country')" label-for="country">
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <v-select
                          v-if="finishedLoading"
                          v-model="userData.country"
                          label="name"
                          :reduce="(val) => val.id"
                          :options="countries"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Department -->
                  <b-col md="3">
                    <ValidationProvider name="department">
                      <b-form-group
                        :label="$t('Department')"
                        label-for="department"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="department"
                          v-model="userData.department"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Province -->
                  <b-col md="3">
                    <ValidationProvider name="province">
                      <b-form-group
                        :label="$t('Province')"
                        label-for="province"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="province"
                          v-model="userData.province"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- District -->
                  <b-col md="3">
                    <ValidationProvider name="district">
                      <b-form-group
                        :label="$t('District')"
                        label-for="district"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="district"
                          v-model="userData.district"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Zip Code -->
                  <b-col md="3">
                    <ValidationProvider name="Zip Code">
                      <b-form-group
                        :label="$t('Zip Code')"
                        label-for="zip-code"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="zip-code"
                          v-model="userData.zip_code"
                          name="zip-code"
                          type="number"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Home Phone -->
                  <b-col md="3">
                    <ValidationProvider name="HomePhone">
                      <b-form-group
                        :label="$t('Home Phone')"
                        label-for="home-phone"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="home-phone"
                          v-model="userData.home_phone"
                          type="number"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Mobile Phone -->
                  <b-col md="3">
                    <ValidationProvider name="Mobile Phone">
                      <b-form-group
                        :label="$t('Mobile Phone')"
                        label-for="mobile-phone"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="mobile-phone"
                          v-model="userData.mobile_phone"
                          v-mask="'###-###-###'"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Work Phone -->
                  <b-col md="3">
                    <ValidationProvider name="Work Phone">
                      <b-form-group
                        :label="$t('Work Phone')"
                        label-for="work-phone"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="work-phone"
                          v-model="userData.work_phone"
                          v-mask="'(XXX)-XXX-XXXX'"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Work Email -->
                  <b-col md="3">
                    <ValidationProvider name="WorkEmail">
                      <b-form-group
                        :label="$t('Work Email')"
                        label-for="work-email"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="work-email"
                          v-model="userData.work_email"
                          type="email"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <!-- Private Email -->
                  <b-col md="3">
                    <ValidationProvider name="PrivateEmail">
                      <b-form-group
                        :label="$t('Private Email')"
                        label-for="private-email"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="private-email"
                          v-model="userData.private_email"
                          type="email"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <!--EMERGENCY CONTACT -->
          <template>
            <b-card>
              <template #header>
                <b-card-title> {{ $t("EMERGENCY CONTACT") }} </b-card-title>
              </template>
              <template #default>
                <b-row>
                  <b-col md="3">
                    <!-- Contact Name -->
                    <ValidationProvider name="ContactName">
                      <b-form-group
                        :label="$t('Contact Name')"
                        label-for="contact-name1"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="contact-name1"
                          v-model="userData.contact_name1"
                          name="contact-name1"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- ocupation -->
                  <b-col md="3">
                    <ValidationProvider name="ocupation1">
                      <b-form-group
                        :label="$t('Ocupation')"
                        label-for="ocupation1"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="ocupation1"
                          v-model="userData.ocupation1"
                          type="text"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- Contact Phone -->
                  <b-col md="3">
                    <ValidationProvider name="Contact Phone">
                      <b-form-group
                        :label="$t('Contact Phone')"
                        label-for="contact-phone1"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="contact-phone1"
                          v-model="userData.contact_phone1"
                          type="number"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- Relation-->
                  <b-col md="3">
                    <ValidationProvider name="Relation">
                      <b-form-group
                        :label="$t('Relation')"
                        label-for="relation1"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <v-select
                          v-if="finishedLoading"
                          v-model="userData.relation1"
                          :reduce="(val) => val.id"
                          :options="relations"
                        >
                          <template #selected-option="{ name }">
                            <span class="text-truncate">{{ $t(name) }}</span>
                          </template>
                          <template #option="{ name }">
                            <span class="text-truncate">{{ $t(name) }}</span>
                          </template>
                        </v-select>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col md="3">
                    <!-- Contact Name 2 -->
                    <ValidationProvider name="ContactName">
                      <b-form-group
                        :label="$t('Contact Name')"
                        label-for="contact-name2"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="contact-name1"
                          v-model="userData.contact_name2"
                          name="contact-name1"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- ocupation -->
                  <b-col md="3">
                    <ValidationProvider name="ocupation2">
                      <b-form-group
                        :label="$t('Ocupation')"
                        label-for="ocupation2"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="ocupation2"
                          v-model="userData.ocupation2"
                          type="text"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- MiddleName -->
                  <b-col md="3">
                    <ValidationProvider name="Contact Phone">
                      <b-form-group
                        :label="$t('Contact Phone')"
                        label-for="contact-phone2"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="contact-phone2"
                          v-model="userData.contact_phone2"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- LastName -->
                  <b-col md="3">
                    <!-- Doc Type-->
                    <ValidationProvider name="Relation2">
                      <b-form-group
                        :label="$t('Relation')"
                        label-for="relation2"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <v-select
                          v-if="finishedLoading"
                          v-model="userData.relation2"
                          :reduce="(val) => val.id"
                          :options="relations"
                        >
                          <template #selected-option="{ name }">
                            <span class="text-truncate">{{ $t(name) }}</span>
                          </template>
                          <template #option="{ name }">
                            <span class="text-truncate">{{ $t(name) }}</span>
                          </template>
                        </v-select>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>

          <!--JOB DETAILS -->
          <template>
            <b-card>
              <template #header>
                <b-card-title> {{ $t("JOB DETAILS") }} </b-card-title>
              </template>
              <template #default>
                <b-row>
                  <!--  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="type_form"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Type Form')"
                        label-for="type_form"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.type_form"
                          :options="types_form"
                          id="type_form"
                          :reduce="(val) => val.id"
                          label="value"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col> -->
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="personal_type"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Personal Type')"
                        label-for="personal_type"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.personal_type"
                          :options="personalTypes"
                          id="personal_type"
                          :reduce="(val) => val.id"
                          @input="changePersonalType"
                        >
                          <template #selected-option="{ value }">
                            <span class="text-truncate">{{ $t(value) }}</span>
                          </template>
                          <template #option="{ value }">
                            <span class="text-truncate">{{ $t(value) }}</span>
                          </template>
                        </v-select>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="entity_group_id"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('entity group') | capitalize"
                        label-for="entity_group_id"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.entity_group_id"
                          :options="entityGroups"
                          id="entity_group_id"
                          :reduce="(val) => val.value"
                          label="text"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3" v-if="userData.personal_type == 2">
                    <!-- First Name -->
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="license_number"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('License Number')"
                        label-for="license_number"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <b-form-input
                          v-if="finishedLoading"
                          id="license_number"
                          v-model="userData.license_number"
                          name="license_number"
                          autofocus
                          :state="errors[0] ? false : valid ? true : null"
                          trim
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <!-- <b-col md="3" v-if="userData.personal_type == 2">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="service_type"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Service Type')"
                        label-for="service_type"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.service_type"
                          :options="serviceTypes"
                          :reduce="(val) => val.id"
                          label="value"
                          id="service_type"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col> -->
                  <!-- <b-col md="3" v-if="userData.personal_type == 2">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="bono"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Bono')"
                        label-for="bono"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.bono"
                          :options="bono"
                          :reduce="(val) => val.id"
                          label="value"
                          id="bono"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col> -->
                  <!-- <b-col md="3" v-if="userData.personal_type == 2">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="category"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Category')"
                        label-for="category"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.category"
                          :options="categories"
                          :reduce="(val) => val.id"
                          label="value"
                          id="category"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col> -->
                  <!-- <b-col md="3" v-if="userData.personal_type == 2">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="ut_type"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('UT Types')"
                        label-for="ut_type"
                        :state="errors[0] ? false : null"
                      >
                        <v-select
                          v-model="userData.ut_type"
                          :options="ut_types"
                          :reduce="(val) => val.id"
                          label="value"
                          id="bono"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col> -->
                  <b-col md="3">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Start Date"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Start Date')"
                        label-for="start_date"
                        :state="errors[0] ? true : null"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <kendo-datepicker
                          v-if="finishedLoading"
                          id="start_data"
                          class=""
                          :class="[
                            errors[0]
                              ? 'border-danger w-auto rounded bg-transparent k-picker-custom border-error-datepicker picker-select-date custom-caro'
                              : 'w-auto rounded bg-transparent k-picker-custom picker-select-date custom-caro',
                            { 'text-white': isDarkSkin },
                            'custom-caro',
                          ]"
                          :format="'MM/dd/yyyy'"
                          v-model="userData.start_date"
                          v-mask="'##/##/####'"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <ValidationProvider
                      name="End Date"
                      v-slot="{ errors }"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('End Date')"
                        label-for="end_date"
                        :state="errors[0] ? true : null"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <kendo-datepicker
                          v-if="finishedLoading"
                          id="end_date"
                          :class="[
                            errors[0]
                              ? 'border-danger w-auto rounded bg-transparent k-picker-custom border-error-datepicker picker-select-date custom-caro'
                              : 'w-auto rounded bg-transparent k-picker-custom picker-select-date custom-caro',
                            { 'text-white': isDarkSkin },
                            'custom-caro',
                          ]"
                          :format="'MM/dd/yyyy'"
                          v-model="userData.end_date"
                          v-mask="'##/##/####'"
                          :state="errors[0] ? false : null"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <!-- Area-->
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="area"
                      rules="required"
                    >
                      <b-form-group
                        :label="$t('Area')"
                        label-for="area"
                        :state="errors[0] ? false : null"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <v-select
                          v-if="finishedLoading"
                          v-model="userData.area"
                          label="name"
                          :reduce="(val) => val.id"
                          :options="departments"
                          @input="getRolByDepartment"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                  <b-col md="3">
                    <!-- Rol-->
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Rol"
                      rules="required"
                    >
                      <b-form-group
                        label="Rol"
                        label-for="Rol"
                        :state="errors[0] ? false : null"
                      >
                        <b-skeleton v-if="!finishedLoading" type="input" />
                        <v-select
                          v-if="finishedLoading"
                          v-model="userData.role"
                          label="name"
                          :reduce="(val) => val.id"
                          :options="roles"
                        />
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>
                </b-row>
              </template>
            </b-card>
          </template>
        </b-form>
      </ValidationObserver>
    </template>

    <template #footer>
      <b-row class="px-3 py-2">
        <b-col lg="6">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-info"
            @click="resetForm"
            >{{ $t("RESET") }}
          </b-button>
        </b-col>
        <b-col lg="6">
          <div class="d-flex justify-content-end">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              class="mr-2"
              @click="createEmployee"
            >
              <span> {{ $t("SAVE") }}</span>
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-danger"
              @click="closeSidebar(false)"
              >{{ $t("CANCEL") }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-sidebar>
</template>

<script>
import { mapGetters } from "vuex";

import EmployeeService from "@/views/rrhh/views/employees/services/employee.service.js";
import Ripple from "vue-ripple-directive";
import axios from "axios";

export default {
  directives: {
    Ripple,
  },
  data() {
    return {
      ownControl: false,
      userData: {
        file: "",
        first_name: null,
        last_name: null,
      },
      finishedLoading: false,
      gender: [
        { id: 1, gender: "Female" },
        { id: 2, gender: "Male" },
      ],
      personalTypes: [
        { id: 1, value: "Employee" },
        { id: 2, value: "Driver" },
        { id: 3, value: "Third Party" },
      ],
      countries: [],
      entityGroups: [],
      relations: [],
      departments: [],
      maritals: [],
      documentTypes: [],
      roles: [],
      disableInput: false,
      docTypeSelected: false,
    };
  },
  created() {},
  async mounted() {
    this.ownControl = true;
    await Promise.all([
      this.getAllDocumentTypes(),
      this.getAllDepartments(),
      this.getAllMartial(),
      this.getAllRelations(),
      this.getEntityGroups(),
      this.getCountries(),
    ]);
    this.finishedLoading = true;
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
  },
  watch: {
    "userData.document_number": function (value) {
      this.disableInput = false;
      this.userData.first_name = null;
      this.userData.last_name = null;
      if (this.userData.document_type == 1 && value.length == 8) {
        this.searchData(value);
      } else if (this.userData.document_type == 2 && value.length == 11) {
        this.searchData(value);
      }
    },
    "userData.document_type": function () {
      this.docTypeSelected = true;
    },
  },
  methods: {
    async searchData(value) {
      this.addPreloader();
      const getForDni = `https://dniruc.apisperu.com/api/v1/dni/${value}?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJlbWFpbCI6ImJvZ3VpcnJlQGdtYWlsLmNvbSJ9.oAtXJNjZbJr6In7Lou-8mN4996MRachznGrhc6qCQwc`;
      const getForRuc = `https://dniruc.apisperu.com/api/v1/ruc/${value}?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJlbWFpbCI6ImJvZ3VpcnJlQGdtYWlsLmNvbSJ9.oAtXJNjZbJr6In7Lou-8mN4996MRachznGrhc6qCQwc`;
      let url = null;
      if (this.userData.document_type == 1) {
        url = getForDni;
      } else if (this.userData.document_type == 2) {
        url = getForRuc;
      }
      try {
        const { data } = await axios.get(url);
        console.log(data);
        if (data.success) {
          this.disableInput = true;
          this.userData.first_name = data.nombres;
          this.userData.last_name =
            data.apellidoPaterno + " " + data.apellidoMaterno;
        } else {
          this.showToastError("Los datos no fueron encontrados");
        }
        this.removePreloader();
      } catch (error) {
        this.showErrorSwal(error);
        this.removePreloader();
      }
    },
    closeSidebar(action) {
      this.$emit("hideSidebar", action);
    },
    resetForm() {
      this.userData = { file: "" };
    },
    async createEmployee() {
      try {
        const result = await this.$refs["form-employee"].validate();
        if (result) {
          const confirm = await this.showConfirmSwal();
          if (confirm.isConfirmed) {
            const params = {
              /* Personal Information */
              first_name: this.userData.first_name,
              last_name: this.userData.last_name,
              document_type: this.userData.document_type,
              document_number: this.userData.document_number,
              dob: this.userData.dob,
              gender: this.userData.gender,
              martial: this.userData.martial,
              blood_type: this.userData.blood_type,
              namedoc: this.userData.namedoc,
              size: this.userData.size,
              image: this.userData.image,

              /* Contact Information */
              address: this.userData.address,
              country: this.userData.country,
              department: this.userData.department,
              province: this.userData.province,
              district: this.userData.district,
              zip_code: this.userData.zip_code,
              home_phone: this.userData.home_phone,
              mobile_phone: this.userData.mobile_phone,
              work_phone: this.userData.work_phone,
              work_email: this.userData.work_email,
              private_email: this.userData.private_email,

              /* Emergency Contact */
              contact_name1: this.userData.contact_name1,
              ocupation1: this.userData.ocupation1,
              contact_phone1: this.userData.contact_phone1,
              relation1: this.userData.relation1,
              contact_name2: this.userData.contact_name2,
              ocupation2: this.userData.ocupation2,
              contact_phone2: this.userData.contact_phone2,
              relation2: this.userData.relation2,

              /* Job Detail */
              // type_form: this.userData.type_form,
              personal_type: this.userData.personal_type,
              entity_group_id: this.userData.entity_group_id,
              license_number: this.userData.license_number,
              // service_type: this.userData.service_type,
              // is_bono: this.userData.bono,
              // category: this.userData.category,
              // ut_type: this.userData.ut_type,
              start_date: this.userData.start_date,
              end_date: this.userData.end_date,
              area: this.userData.area,
              role: this.userData.role,

              user_id: this.currentUser.user_id,
              // doe: this.userData.doe,
            };
            const data = await EmployeeService.createEmployee(params);
            if (data.status === 200) {
              if (data.data[0].ERROR === 1) {
                // this.showWarningSwal("The Number Doc Already exists!");
              } else {
                // this.$emit("refresh");
                // this.spinnerOn = false;
                // this.showSuccessSwal();
                // this.closeModal();
              }
            }
          }
        }
      } catch (e) {
        console.log(e);
        // this.spinnerOn = false;
        // this.showErrorSwal(e);
        // return [];
      }
    },
    async getAllDocumentTypes() {
      try {
        const { data } = await EmployeeService.getAllDocumentTypes();
        this.documentTypes = data;
        return this.documentTypes;
      } catch (e) {
        console.log(e);
        // this.showErrorSwal(e);
        return [];
      }
    },
    async getAllMartial() {
      try {
        const { data } = await EmployeeService.getAllMartial();
        this.maritals = data;
        return this.maritals;
      } catch (e) {
        this.showErrorSwal(e);
        return [];
      }
    },
    async getAllDepartments() {
      try {
        const { data } = await EmployeeService.getAllAreas({
          moduleId: this.currentUser.modul_id,
        });
        this.departments = data;
        return this.areas;
      } catch (e) {
        // this.showErrorSwal(e);
        console.log(e);
        return [];
      }
    },
    async getAllRelations() {
      try {
        const { data } = await EmployeeService.getAllRelations();
        this.relations = data;
        return this.relations;
      } catch (e) {
        this.showErrorSwal(e);
        return [];
      }
    },
    async getRolByDepartment() {
      try {
        const params = {
          id: this.userData.area,
        };
        const { data } = await EmployeeService.getRolByDepartment(params);
        this.roles = data;
        return this.roles;
      } catch (e) {
        // this.showErrorSwal(e);
        console.log(e);
        return [];
      }
    },
    async getEntityGroups() {
      try {
        const { data } = await EmployeeService.getEntitiesGroup();
        this.entityGroups = data;
        return this.entityGroups;
      } catch (error) {
        console.log(error);
      }
    },
    async getCountries() {
      try {
        const { data } = await EmployeeService.getCountries();
        this.countries = data;
        return this.countries;
      } catch (error) {
        console.log(error);
      }
    },
    changePersonalType() {
      if (this.userData.personal_type != 2) {
        this.userData.service_type = null;
        this.userData.bono = null;
        this.userData.license_number = null;
        this.userData.ut_type = null;
      }
    },
    onImageChange(e) {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(e.target.files[0]);
      fileReader.onload = (e) => {
        this.userData.image = e.target.result;
      };
      const filearch = e.target.files[0];

      const file = e.target.files[0].name;

      const { size } = e.target.files[0];
      this.userData.namedoc = file;
      this.userData.size = size;
      this.userData.file = URL.createObjectURL(filearch);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.v-select.drop-up.vs--open .vs__dropdown-toggle {
  border-radius: 0 0 4px 4px;
  border-top-color: transparent;
  border-bottom-color: rgba(60, 60, 60, 0.26);
}

[data-popper-placement="top"] {
  border-radius: 4px 4px 0 0;
  border-top-style: solid;
  border-bottom-style: none;
  box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.15);
}

#overpx .vs__dropdown-toggle .vs__selected-options .vs__search {
  margin: 9.15px 0 0 !important;
}

#add-new-employer-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}

.sidebar-xl {
  width: 90rem !important;
}

.b-sidebar-header {
  flex-direction: row-reverse !important;
  justify-content: space-between !important;

  .close {
    margin-right: revert !important;
  }
}
.custom-caro.k-input {
  max-width: 218px !important;
  width: 300px !important;
}

.custom-caro22.k-input {
  max-width: 157px !important;
  width: 300px !important;
}
</style>